import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  output,
  resource,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrLoadingState } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { DeselectSingleSelectedDirective } from 'src/app/directives/deselect-single-selected.directive';

import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BaseDataRoute, BaseItemsHandler } from '../baseitems-wrapper.component';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { BaseItem } from '../baseitems-wrapper.component';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-baseitems-modal',
  templateUrl: './baseitems-modal.component.html',
  styleUrl: './baseitems-modal.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DeselectSingleSelectedDirective,
    HideIDColumnsPipe,
    MrTranslatePipe,
    TablePrettyPrintPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseitemsModalComponent implements BaseItemsHandler {
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);

  readonly refresh = output<BaseItem[]>();
  readonly isModalOpen = model<boolean>();
  readonly route = model.required<BaseDataRoute>();

  protected readonly preSelected = computed(() => this.route().selected);
  protected readonly tabelle = computed(() => this.route().tabelle);
  protected readonly title = computed(() => this.route().title);
  protected readonly sub_id = computed(() => this.route().sub_id);

  protected readonly isRep = this.apiService.isRep;
  protected readonly bezeichnung = new FormControl('', Validators.required);
  protected readonly selectedRow = linkedSignal<BaseItem | null>(() => (this.isModalOpen(), null));

  private readonly tableResource = resource({
    params: () => this.isModalOpen() || undefined,
    loader: () => this.apiService.getBaseItems(this.tabelle(), this.sub_id()),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly table = this.tableResource.value;
  protected readonly isLoading = this.tableResource.isLoading;
  protected readonly loadState = signal(ClrLoadingState.DEFAULT);
  private isChanged = false;

  constructor() {
    effect(() => this.bezeichnung.reset(this.selectedRow()?.Bezeichnung));

    effect(() => {
      const preSelect = this.preSelected();
      if (preSelect && this.table().columns.length)
        untracked(() => {
          this.route.update(route => ({ ...route, selected: null }));
          switch (typeof preSelect) {
            case 'string': {
              const found = this.table().rows.find(row => row.Bezeichnung == preSelect);
              if (found) this.selectedRow.set(found);
              else this.bezeichnung.setValue(preSelect);
              break;
            }
            case 'number': {
              this.selectedRow.set({ ID: preSelect } as BaseItem);
              break;
            }
          }
        });
    });
    effect(() => {
      const status = this.tableResource.status();
      untracked(() => {
        if (status == 'reloading') {
          this.isChanged = true;
        } else if (status == 'resolved' && this.isChanged) {
          this.isChanged = false;
          this.refresh.emit(this.table().rows);
        }
      });
    });
  }

  protected async onSubmit() {
    this.loadState.set(ClrLoadingState.LOADING);
    const sendObj: BaseItem = {
      ID: this.selectedRow()?.ID,
      Bezeichnung: this.bezeichnung.value,
    };

    try {
      const id = await this.apiService.setBaseItems(this.tabelle(), sendObj, this.sub_id());

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.tableResource.reload();
      this.selectedRow.set({ ID: id } as BaseItem);
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteRow() {
    try {
      await this.apiService.deleteBaseItems(this.tabelle(), this.selectedRow().ID);

      this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.tableResource.reload();
      this.selectedRow.set(null);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
}
